import { apiGet, apiPatch, apiPost } from './client.js';

export type PaymentMethod =
  | 'cash'
  | 'card'
  | 'store_credit'
  | 'loyalty_points'
  | 'check'
  | 'other';

export type PaymentCardType = 'visa' | 'mastercard' | 'amex' | 'discover' | 'debit' | 'other';

export type PaymentStaffRef = {
  id: string;
  firstName: string;
  lastName: string;
};

export type PaymentRecord = {
  id: string;
  ticketId?: string;
  method: string;
  amount: string;
  status: string;
  cashTendered?: string | null;
  changeDue?: string | null;
  transactionId?: string | null;
  cardType?: string | null;
  lastFour?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  blockyypTransactionId?: string | null;
  processedManually?: boolean | null;
  cashDiscountApplied?: boolean | null;
  cashDiscountAmount?: string | null;
  isDeposit?: boolean | null;
  notes?: string | null;
  processedBy?: string | null;
  recordedBy?: PaymentStaffRef | null;
  /** Business payment date — reports key off this. */
  paidAt: string;
  createdAt: string;
  voidedAt?: string | null;
  voidedBy?: PaymentStaffRef | null;
  voidReason?: string | null;
  editedAt?: string | null;
  editedBy?: PaymentStaffRef | null;
  editNotes?: string | null;
  originalAmount?: string | null;
  originalMethod?: string | null;
};

export type EditPaymentInput = {
  amount?: number;
  method?: PaymentMethod | string;
  paidAt?: string;
  acknowledgeClosedSession?: boolean;
  transactionId?: string | null;
  cardType?: PaymentCardType | string | null;
  lastFour?: string | null;
  notes?: string | null;
  editNotes?: string;
  managerOverrideAuthorizedBy?: string;
};

export type VoidPaymentInput = {
  reason: string;
  ticketAction: 'reopen' | 'keep_closed';
  managerOverrideAuthorizedBy: string;
};

export type VoidPaymentResult = {
  payment: PaymentRecord;
  ticket?: {
    id: string;
    ticketNumber?: string;
    status?: string;
    paidTotal?: string;
    balanceDue?: string;
  };
};

export interface CheckoutPaymentInput {
  method: PaymentMethod;
  amount: number;
  tipAmount?: number;
  tipPin?: string;
  cashTendered?: number;
  notes?: string;
  transactionId?: string;
  cardType?: PaymentCardType;
  lastFour?: string;
  paidAt?: string;
  acknowledgeClosedSession?: boolean;
  managerOverrideAuthorizedBy?: string;
}

export interface CheckoutInput {
  ticketId: string;
  payments: CheckoutPaymentInput[];
  storeCreditApplied?: number;
  loyaltyPointsRedeemed?: number;
  receiptMethod?: 'print' | 'email' | 'sms' | 'none';
  postWaiverSigned?: boolean;
}

export interface CheckoutResponse {
  ticket: unknown;
  paymentsCreated: unknown[];
  pointsEarned: number;
  storeCreditUsed: string;
  cashSessionUpdated?: boolean;
  cashSessionWarning?: string | null;
}

export interface QuickSaleInput {
  customerId?: string;
  items: Array<{
    inventoryItemId?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    originalPrice?: number;
    discountAmount?: number;
    discountPct?: number;
    discountReason?: string;
    discountPresetId?: string | null;
    taxable?: boolean;
    type: 'retail' | 'custom';
  }>;
  /** @deprecated Prefer `payments` — kept for older callers */
  payment?: CheckoutPaymentInput;
  payments?: CheckoutPaymentInput[];
  storeCreditApplied?: number;
  loyaltyPointsRedeemed?: number;
  cartDiscountAmount?: number;
  cartDiscountReason?: string;
  cartDiscountPresetId?: string | null;
  /** Manager user id from POST /users/pin/verify-override */
  managerOverrideAuthorizedBy?: string;
}

export interface QuickSaleResponse {
  ticket: { id: string; ticketNumber: string; total: string };
  payment: {
    id: string;
    method: string;
    amount: string;
    cashTendered?: string | null;
    changeDue?: string | null;
  };
  paymentsCreated?: Array<{
    id: string;
    method: string;
    amount: string;
    cashTendered?: string | null;
    changeDue?: string | null;
    transactionId?: string | null;
    cardType?: string | null;
    lastFour?: string | null;
  }>;
  lineItems: Array<{
    id: string;
    description: string;
    quantity: string;
    unitPrice: string;
    discountAmount?: string | null;
    discountPct?: string | null;
    discountReason?: string | null;
    lineTotal: string;
  }>;
  receipt: {
    subtotal: string;
    itemDiscountTotal: string;
    discountedSubtotal: string;
    cartDiscountAmount: string;
    cartDiscountReason: string | null;
    taxableAmount: string;
    taxRate: string;
    taxTotal: string;
    total: string;
    youSaved: string;
  };
  pointsEarned: number;
  changeDue: string;
  sessionUpdated?: boolean;
  cashSessionUpdated?: boolean;
  cashSessionWarning?: string | null;
}

export function checkout(body: CheckoutInput) {
  return apiPost<CheckoutResponse, CheckoutInput>('/api/v1/payments/checkout', body).then(
    (res) => res.data,
  );
}

export function quickSale(body: QuickSaleInput) {
  return apiPost<QuickSaleResponse, QuickSaleInput>('/api/v1/payments/quick-sale', body).then(
    (res) => res.data,
  );
}

export function listPaymentsByTicket(ticketId: string) {
  return apiGet<PaymentRecord[]>('/api/v1/payments', { params: { ticketId } }).then(
    (res) => res.data,
  );
}

export function editPayment(paymentId: string, body: EditPaymentInput) {
  return apiPatch<PaymentRecord, EditPaymentInput>(`/api/v1/payments/${paymentId}`, body).then(
    (res) => res.data,
  );
}

export function voidPayment(paymentId: string, body: VoidPaymentInput) {
  return apiPost<VoidPaymentResult, VoidPaymentInput>(
    `/api/v1/payments/${paymentId}/void`,
    body,
  ).then((res) => res.data);
}
